import React, { useMemo, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { toast } from "react-toastify";

import { CrmPetController } from "../../../services/controllers/crmPet";
import { buildContactMessage, isMembershipCovered } from "../../../services/formatResponses/crmPet";
import { formatTutorName } from "../../../services/functions";
import { useCrmPet } from "../../../store/crmPet/useCrmPet";
import { WHATSAPP_SEND_MESSAGE_BASE } from "../../../styles/constant";

import { WhatsAppIcon } from "../icons/WhatsAppIcon";

export function WhatsAppModal({ customer, config, onClose }) {
  const CCrmPet = CrmPetController();
  const contactSubmitting = useCrmPet((state) => state.data.contactSubmitting);
  const availablePetIdsByMembership = useMemo(() => new Map(
    customer.memberships.map((membership) => [
      membership.membershipId,
      membership.petIds.filter((petId) => customer.pets.some((pet) => pet.petId === petId && !pet.blocked)),
    ]),
  ), [customer.memberships, customer.pets]);
  const availableMembershipIds = customer.memberships
    .filter((membership) => availablePetIdsByMembership.get(membership.membershipId)?.length)
    .map((membership) => membership.membershipId);
  const [selectedMembershipIds, setSelectedMembershipIds] = useState(availableMembershipIds);
  const [selectedPetIds, setSelectedPetIds] = useState(() => [...new Set(
    availableMembershipIds.flatMap((membershipId) => availablePetIdsByMembership.get(membershipId) || []),
  )]);
  const [editedMessage, setEditedMessage] = useState(null);

  const selectedMemberships = customer.memberships.filter((membership) => (
    selectedMembershipIds.includes(membership.membershipId)
  ));
  const selectedPets = customer.pets.filter((pet) => selectedPetIds.includes(pet.petId));
  const generatedMessage = buildContactMessage({
    customer,
    memberships: selectedMemberships,
    pets: selectedPets,
    config,
  });
  const finalMessage = editedMessage !== null ? editedMessage : generatedMessage;
  const allMembershipsCovered = selectedMemberships.every((membership) => (
    isMembershipCovered(membership, selectedPetIds)
  ));
  const phone = (customer.customerPhone || "").replace(/\D/g, "");
  const canSend = Boolean(
    phone &&
    customer.customerId &&
    selectedMemberships.length &&
    selectedPets.length &&
    allMembershipsCovered &&
    finalMessage.trim() &&
    finalMessage.length <= 4096 &&
    !contactSubmitting
  );

  const toggleMembership = (membership) => {
    const membershipId = membership.membershipId;
    const isSelected = selectedMembershipIds.includes(membershipId);
    if (isSelected) {
      const nextMembershipIds = selectedMembershipIds.filter((id) => id !== membershipId);
      const stillRelatedPetIds = new Set(nextMembershipIds.flatMap((id) => availablePetIdsByMembership.get(id) || []));
      setSelectedMembershipIds(nextMembershipIds);
      setSelectedPetIds((current) => current.filter((petId) => stillRelatedPetIds.has(petId)));
    } else {
      const relatedPetIds = availablePetIdsByMembership.get(membershipId) || [];
      if (!relatedPetIds.length) return;
      setSelectedMembershipIds((current) => [...current, membershipId]);
      setSelectedPetIds((current) => [...new Set([...current, ...relatedPetIds])]);
    }
    setEditedMessage(null);
  };

  const togglePet = (petId) => {
    if (!selectedPetIds.includes(petId)) {
      setSelectedPetIds((current) => [...current, petId]);
      setEditedMessage(null);
      return;
    }
    const nextPetIds = selectedPetIds.filter((id) => id !== petId);
    const orphanedMembership = selectedMemberships.find((membership) => !isMembershipCovered(membership, nextPetIds));
    if (orphanedMembership) {
      toast.error(`A régua ${orphanedMembership.label} precisa de ao menos um pet relacionado.`, { theme: "colored" });
      return;
    }
    setSelectedPetIds(nextPetIds);
    setEditedMessage(null);
  };

  const handleSend = async () => {
    if (!canSend) return;
    const popup = window.open("", "_blank");
    if (!popup) {
      toast.error("Permita pop-ups para abrir o WhatsApp.", { theme: "colored" });
      return;
    }
    popup.document.title = "Abrindo WhatsApp...";
    popup.document.body.innerHTML = "<p style='font-family: sans-serif'>Registrando contato...</p>";
    const result = await CCrmPet.registerUnifiedContact(
      customer,
      selectedPetIds,
      selectedMembershipIds,
      finalMessage,
    );
    if (!result) {
      popup.close();
      return;
    }
    const waUrl = WHATSAPP_SEND_MESSAGE_BASE
      .replace("[PHONE_NUMBER]", phone)
      .replace("[MESSAGE]", encodeURIComponent(finalMessage));
    popup.opener = null;
    popup.location.replace(waUrl);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="crm-pet-whatsapp-title">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <p id="crm-pet-whatsapp-title" className="m-0 flex items-center gap-2 text-sm font-semibold">
            <WhatsAppIcon size={16} /> WhatsApp para {formatTutorName(customer.customerName)}
          </p>
          <button type="button" onClick={onClose} disabled={contactSubmitting} aria-label="Fechar" className="border-none bg-transparent text-gray-400"><X size={18} /></button>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          <SelectionBox title="Réguas desta mensagem" hint={`${selectedMemberships.length} selecionada(s)`}>
            {customer.memberships.map((membership) => {
              const disabled = !availablePetIdsByMembership.get(membership.membershipId)?.length;
              return (
                <CheckOption
                  key={membership.membershipId}
                  checked={selectedMembershipIds.includes(membership.membershipId)}
                  disabled={disabled || contactSubmitting}
                  onChange={() => toggleMembership(membership)}
                  label={membership.label}
                  detail={disabled ? "Sem pet disponível" : membership.contactCount ? `${membership.contactCount} contato(s)` : "Pendente"}
                />
              );
            })}
          </SelectionBox>

          <SelectionBox title="Pets relacionados" hint={`${selectedPets.length} selecionado(s)`}>
            {customer.pets.map((pet) => {
              const related = selectedMemberships.some((membership) => membership.petIds.includes(pet.petId));
              return (
                <CheckOption
                  key={pet.petId}
                  checked={selectedPetIds.includes(pet.petId)}
                  disabled={pet.blocked || !related || contactSubmitting}
                  onChange={() => togglePet(pet.petId)}
                  label={pet.petName || "Sem nome"}
                  detail={pet.blocked ? "Bloqueado" : null}
                />
              );
            })}
          </SelectionBox>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">Mensagem</p>
              {editedMessage !== null && (
                <button type="button" onClick={() => setEditedMessage(null)} className="border-none bg-transparent text-xs text-scale-500 underline">Restaurar</button>
              )}
            </div>
            <textarea
              rows={7}
              maxLength={4096}
              value={finalMessage}
              disabled={contactSubmitting}
              onChange={(event) => setEditedMessage(event.target.value)}
              className="w-full resize-y rounded-lg border border-zinc-200 bg-gray-50 p-3 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-scale-300"
            />
            <p className={`m-0 mt-1 text-[11px] ${finalMessage.length > 4096 ? "text-red-600" : "text-gray-400"}`}>
              {finalMessage.length}/4096 caracteres
            </p>
          </div>

          {!phone && <p className="m-0 rounded bg-red-50 px-3 py-2 text-xs text-red-600">Cliente sem telefone cadastrado.</p>}
          {!allMembershipsCovered && <p className="m-0 rounded bg-amber-50 px-3 py-2 text-xs text-amber-700">Cada régua selecionada precisa ter ao menos um pet relacionado.</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-zinc-200 bg-gray-50 px-5 py-4">
          <button type="button" onClick={onClose} disabled={contactSubmitting} className="border-none bg-transparent px-4 py-2 text-sm text-gray-600">Cancelar</button>
          <button type="button" onClick={handleSend} disabled={!canSend} className="flex items-center gap-2 rounded-lg border-none bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50">
            {contactSubmitting ? <RefreshCw size={14} className="animate-spin" /> : <WhatsAppIcon size={14} />}
            {contactSubmitting ? "Registrando..." : "Abrir WhatsApp"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SelectionBox({ title, hint, children }) {
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200">
      <div className="flex items-center justify-between bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700">
        <span>{title}</span><span className="font-normal text-gray-400">{hint}</span>
      </div>
      <div className="grid gap-2 p-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function CheckOption({ checked, disabled, onChange, label, detail }) {
  return (
    <label className={`flex items-center gap-2 text-xs ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} className="h-4 w-4 accent-scale-500" />
      <span className="flex-1 text-gray-700">{label}</span>
      {detail && <span className="text-[10px] text-gray-400">{detail}</span>}
    </label>
  );
}
